import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { normalizeRole } from "../lib/permissions";

const AuthContext = createContext(null);

function decodeJwtPayload(token) {
  if (!token) return null;

  const [, payload] = String(token).split(".");
  if (!payload || typeof window === "undefined" || typeof window.atob !== "function") {
    return null;
  }

  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded));
  } catch (_error) {
    return null;
  }
}

function hasGrowthMarker(...values) {
  return values.some((value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();

    return normalized === "growth" || normalized === "super admin" || normalized === "super_admin" || normalized === "super-admin";
  });
}

function readOrganizationId(source) {
  if (!source) return null;

  const candidates = [
    source.organization_id,
    source.organizationId,
    source.org_id,
    source.orgId,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
}

function hasRoleMetadata(currentUser) {
  if (!currentUser) return false;

  const candidates = [
    currentUser.app_metadata?.role,
    currentUser.app_metadata?.app_role,
    currentUser.app_metadata?.team_label,
    currentUser.app_metadata?.title,
    currentUser.user_metadata?.role,
    currentUser.user_metadata?.app_role,
    currentUser.user_metadata?.team_label,
    currentUser.user_metadata?.title,
  ];

  return candidates.some((candidate) => typeof candidate === "string" && candidate.trim());
}

function resolveProfileRole(profile, currentUser) {
  const profileRole = profile?.role;
  const appRole = currentUser?.app_metadata?.role;
  const appAppRole = currentUser?.app_metadata?.app_role;
  const userRole = currentUser?.user_metadata?.role;
  const userAppRole = currentUser?.user_metadata?.app_role;
  const appTeamLabel = currentUser?.app_metadata?.team_label;
  const userTeamLabel = currentUser?.user_metadata?.team_label;
  const appTitle = currentUser?.app_metadata?.title;
  const userTitle = currentUser?.user_metadata?.title;
  const profileTitle = profile?.title;
  const profileTeamLabel = profile?.team_label;

  if (
    hasGrowthMarker(
      profileRole,
      profileTitle,
      profileTeamLabel,
      appRole,
      appAppRole,
      appTeamLabel,
      appTitle,
      userRole,
      userAppRole,
      userTeamLabel,
      userTitle
    )
  ) {
    return "Growth";
  }

  return normalizeRole(
    profileRole ||
    appRole ||
    appAppRole ||
    userRole ||
    userAppRole ||
    "Viewer"
  );
}

const buildFallbackProfile = (currentUser) => ({
  id: currentUser.id,
  email: currentUser.email,
  full_name:
    currentUser.user_metadata?.full_name ||
    currentUser.email?.split("@")[0] ||
    "SpaceM8 User",
  role: resolveProfileRole(null, currentUser),
  status: "Active",
  organization_id:
    readOrganizationId(currentUser.app_metadata) ||
    readOrganizationId(currentUser.user_metadata) ||
    null,
});

const ensureProfile = (profile, currentUser) => ({
  ...(profile || buildFallbackProfile(currentUser)),
  id: currentUser.id,
  email: profile?.email || currentUser.email,
  full_name:
    profile?.full_name ||
    currentUser.user_metadata?.full_name ||
    currentUser.email?.split("@")[0] ||
    "SpaceM8 User",
  role: resolveProfileRole(profile, currentUser),
  status: profile?.status || "Active",
  organization_id:
    profile?.organization_id ||
    readOrganizationId(currentUser.app_metadata) ||
    readOrganizationId(currentUser.user_metadata) ||
    null,
});

const createMissingProfile = (currentUser) => ({
  id: currentUser.id,
  email: currentUser.email,
  full_name:
    currentUser.user_metadata?.full_name ||
    currentUser.email?.split("@")[0] ||
    "SpaceM8 User",
  role: resolveProfileRole(null, currentUser),
  status: "Active",
  organization_id:
    readOrganizationId(currentUser.app_metadata) ||
    readOrganizationId(currentUser.user_metadata) ||
    null,
});

const withTimeout = (promise, milliseconds = 6000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Supabase profile request timed out")), milliseconds)
    ),
  ]);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      return;
    }

    setProfile(buildFallbackProfile(currentUser));

    try {
      const { data, error } = await withTimeout(
        supabase
          .from("user_profiles")
          .select("*")
          .eq("id", currentUser.id)
          .maybeSingle()
      );

      if (!error && data) {
        setProfile(ensureProfile(data, currentUser));
        return;
      }

      if (currentUser.email) {
        const { data: emailProfile, error: emailProfileError } = await withTimeout(
          supabase
            .from("user_profiles")
            .select("*")
            .eq("email", currentUser.email)
            .maybeSingle()
        );

        if (!emailProfileError && emailProfile) {
          setProfile(ensureProfile(emailProfile, currentUser));
          return;
        }
      }

      const nextProfile = createMissingProfile(currentUser);
      const { data: createdProfile } = await withTimeout(
        supabase.from("user_profiles").insert([nextProfile]).select().maybeSingle()
      );

      setProfile(ensureProfile(createdProfile || nextProfile, currentUser));
    } catch (_error) {
      setProfile(buildFallbackProfile(currentUser));
    }
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSession(null);
      setUser(null);
      setProfile(null);
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    let lastSessionUserId = null;
    let lastClaimsRefreshAttemptUserId = null;

    const syncSessionState = (nextSession) => {
      const nextUser = nextSession?.user || null;
      const nextUserId = nextUser?.id || null;

      if (lastSessionUserId !== nextUserId) {
        lastClaimsRefreshAttemptUserId = null;
      }

      lastSessionUserId = nextUserId;
      setSession(nextSession || null);
      setUser(nextUser);
      setProfile(nextUser ? buildFallbackProfile(nextUser) : null);
      setLoading(false);
      fetchProfile(nextUser);
    };

    const maybeRefreshSessionClaims = async (nextSession) => {
      const nextUser = nextSession?.user || null;

      if (!nextUser) {
        return;
      }

      const hasOrganizationMetadata = Boolean(
        readOrganizationId(nextUser.app_metadata) || readOrganizationId(nextUser.user_metadata)
      );

      if (hasOrganizationMetadata && hasRoleMetadata(nextUser)) {
        return;
      }

      if (lastClaimsRefreshAttemptUserId === nextUser.id) {
        return;
      }

      lastClaimsRefreshAttemptUserId = nextUser.id;

      try {
        const { data, error } = await withTimeout(supabase.auth.refreshSession());

        if (error || !isMounted || !data?.session) {
          return;
        }

        syncSessionState(data.session);
      } catch (_error) {
        // Keep the current session if refresh fails; the profile row fallback still applies.
      }
    };

    const loadSession = async () => {
      try {
        const { data } = await withTimeout(supabase.auth.getSession());
        if (!isMounted) return;

        const activeSession = data.session || null;
        syncSessionState(activeSession);
        void maybeRefreshSessionClaims(activeSession);
      } catch (_error) {
        if (!isMounted) return;
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) {
        return;
      }

      syncSessionState(nextSession);
      void maybeRefreshSessionClaims(nextSession);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email, password) => {
    if (!supabase) {
      throw new Error("SpaceM8 is missing its Supabase configuration.");
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    await fetchProfile(user);
  }, [fetchProfile, user]);

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      sessionClaims: decodeJwtPayload(session?.access_token),
      loading,
      signIn,
      signOut,
      refreshProfile,
    }),
    [session, user, profile, loading, signIn, signOut, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
